import React, { useContext } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import AppLayout from '../../Components/AppLayout';
import AuthLayout from '../../Components/AuthLayout';
import { useAppContext } from '../../Helpers/Contexts/appContext';
import { AuthContext } from '../../Helpers/Contexts/authContext';
import { PrivateRoute, PublicRoute } from '../../Utils/helperFunctions';
import routes from '../../Utils/routes';
import { PORTAL_SCREENS } from '../../Utils/routes';

const MyProfile = React.lazy(() => import('../MyProfile'));
const ProfileDetails = React.lazy(() => import('../MyProfile/ProfileDetails'));

// auth
const Login = React.lazy(() => import('../Auth/Login/index'));
const OnBoarding = React.lazy(() => import('../OnBoarding/index'));
const ConnectSocials = React.lazy(() => import('../Auth/ConnectSocials'));
// home
/* Retained deliberately: /home now renders the investor portal. This import
   is kept so restoring the previous MUI Home is a one-line change, and
   because that container still holds the only working Salesforce-backed fund
   view (see the note at the top of Containers/Home). */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Home = React.lazy(() => import('../Home/index'));
const SpotlightDetails = React.lazy(() => import('../Home/SpotlightDetails'));
// MyPortfolio
const MyPortfolio = React.lazy(() => import('../MyPortfolio'));
const FundPortfolio = React.lazy(() => import('../MyPortfolio/FundPortfolio'));
const RecentReports = React.lazy(() => import('../MyPortfolio/RecentReports'));
const PortfolioDetails = React.lazy(() => import('../../Components/PortfolioDetails/PortfolioDetails'));
const EngagementSummaryDetails = React.lazy(() => import('../../Components/PortfolioDetails/EngagementSummaryDetails'));
// getinvolved
const GetInvolved = React.lazy(() => import('../GetInvolved'));
// startups
const Startups = React.lazy(() => import('../Startups'));
// events
const Events = React.lazy(() => import('../Events/index'));
const EventDetails = React.lazy(() => import('../Events/EventDetails/index'));
const HostEvent = React.lazy(() => import('../Events/HostEvent/index'));
// people
const OurTeam = React.lazy(() => import('../People/OurTeam'));
const OurInvestors = React.lazy(() => import('../People/OurInvestors'));
const PeopleDetails = React.lazy(() => import('../People/components/PeopleDetails'));
// coe
const COE = React.lazy(() => import('../COE'));
const COEDetails = React.lazy(() => import('../COE/COEDetails'));
// juryPortal
const JuryPortal = React.lazy(() => import('../JuryPortal'));
const AssignedStartups = React.lazy(() => import('../JuryPortal/assignedStartups'));
const StartupDetails = React.lazy(() => import('../JuryPortal/StartupDetails'));
// support
const Support = React.lazy(() => import('../Support'));
const TicketPreview = React.lazy(() => import('../Support/components/Ticket/TicketPreview'));
const Answers = React.lazy(() => import('../Support/components/Questions/Answers'));
const FAQs = React.lazy(() => import('../Support/FAQs'));
const FaqQuestions = React.lazy(() => import('../Support/FAQs/FaqQuestions'));

// Turbostart investor portal (TS Intelligence) — the ported prototype.
// Lazy so its 142 KB design system and 131 image assets stay out of the
// initial bundle and only load once the portal is actually opened.
const TurbostartPortal = React.lazy(() => import('../InvestorPortal'));

const MainLayout: React.FunctionComponent = () => {
  useAppContext();

  const {
    state: { user },
  } = useContext(AuthContext);

  // Define roles
  const roles = user?.contactRoles || [];
  const isInvestor = ['Strategic Investor', 'Passive Investor'].some((role) => roles?.includes(role));
  const isJuryMember = roles?.includes('Jury Member');
  const hasAllRoles = isInvestor && isJuryMember;

  return (
    <>
      <Routes>
        {/* Initial Route */}
        <Route path="/" element={<Navigate to={routes.login} />} />

        {/* Public Routes */}
        <Route
          path={routes.login}
          index
          element={
            <PublicRoute>
              <AuthLayout>
                <Login />
              </AuthLayout>
            </PublicRoute>
          }
        />
        {/* <Route
          path={routes.otpverification}
          element={
            <PublicRoute>
              <AuthLayout>
                <VerifyOTP />
              </AuthLayout>
            </PublicRoute>
          }
        />
        <Route
          path={routes.emailverification}
          element={
            <PublicRoute>
              <AuthLayout>
                <EmailConformation />
              </AuthLayout>
            </PublicRoute>
          }
        />
        <Route
          path={routes.forgotPassword}
          element={
            <PublicRoute>
              <AuthLayout>
                <ForgotPassword />
              </AuthLayout>
            </PublicRoute>
          }
        />
        <Route
          path={routes.signup}
          element={
            <PublicRoute>
              <AuthLayout>
                <Signup />
              </AuthLayout>
            </PublicRoute>
          }
        />
        <Route
          path={routes.changePassword}
          element={
            <PublicRoute>
              <AuthLayout>
                <ConfirmPassword />
              </AuthLayout>
            </PublicRoute>
          }
        /> */}

        {/* Private Routes */}
        <Route
          path={routes.onboarding}
          element={
            <PrivateRoute>
              <AuthLayout>
                <OnBoarding />
              </AuthLayout>
            </PrivateRoute>
          }
        />

        <Route
          path={routes.connectSocials}
          element={
            <PrivateRoute>
              <AuthLayout>
                <ConnectSocials />
              </AuthLayout>
            </PrivateRoute>
          }
        />

        {/* My Profile */}
        <Route
          path={routes.profile}
          element={
            <PrivateRoute>
              <AppLayout>
                <MyProfile />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path={routes.profileDetails}
          element={
            <PrivateRoute>
              <AppLayout>
                <ProfileDetails />
              </AppLayout>
            </PrivateRoute>
          }
        />

        {(isInvestor || hasAllRoles) && (
          <>
            {/* Home — the Turbostart investor portal.
                Login redirects to routes.home, so this is the screen an
                investor lands on. The portal renders its own frame, so it is
                NOT wrapped in AppLayout; nesting would paint two sidebars.
                The previous MUI Home component is still in the codebase and
                this is a one-element revert. */}
            <Route
              path={routes.home}
              element={
                <PrivateRoute>
                  <TurbostartPortal />
                </PrivateRoute>
              }
            />

            {/* One route per portal screen, so every screen has a real URL
                (/home/events, /home/analytics, …). They all render the same
                portal, which reads the active screen off the URL; declaring
                them individually rather than as /home/* keeps them STATIC, so
                React Router ranks them above the /home/:Id SpotlightDetails
                route below and that route keeps working unchanged. */}
            {Object.keys(PORTAL_SCREENS).map((slug) => (
              <Route
                key={slug}
                path={`${routes.home}/${slug}`}
                element={
                  <PrivateRoute>
                    <TurbostartPortal />
                  </PrivateRoute>
                }
              />
            ))}

            <Route
              path={`${routes.home}/:Id`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <SpotlightDetails />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            {/* My Portfolio */}
            <Route
              path={routes.myPortfolio}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <MyPortfolio />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            <Route
              path={routes.fundPortfolioDetails}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <FundPortfolio />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            <Route
              path={`${routes.myPortfolio}/:Id`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <PortfolioDetails />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            <Route
              path={routes.recentReports}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <RecentReports />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path={`${routes.engagementrSummaryDetails}`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <EngagementSummaryDetails />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            {/* Startups */}
            <Route
              path={routes.startups}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <Startups />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            <Route
              path={`${routes.startups}/:Id`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <PortfolioDetails />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            {/* Get involved */}
            <Route
              path={routes.getinvolved}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <GetInvolved />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path={`${routes.getinvolved}/:Id`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <PortfolioDetails />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            {/*People */}
            <Route
              path={routes.ourTeam}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <OurTeam />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            <Route
              path={routes.ourInvestors}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <OurInvestors />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            <Route
              path={`${routes.ourTeam}/:eventId`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <PeopleDetails />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path={`${routes.ourInvestors}/:eventId`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <PeopleDetails />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            {/* Events */}
            <Route
              path={routes.events}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <Events />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path={`${routes.eventDetail}/:eventId`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <EventDetails />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path={`${routes.hostEvent}`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <HostEvent />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            {/* COE */}
            <Route
              path={`${routes.coe}`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <COE />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path={`${routes.coe}/:Id`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <COEDetails />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            {/* Support */}
            <Route
              path={`${routes.support}`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <Support />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path={`${routes.Answers}`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <Answers />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path={`${routes.TicketPreview}`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <TicketPreview />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            <Route
              path={`${routes.faqs}`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <FAQs />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            <Route
              path={`${routes.support}/:Id`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <FaqQuestions />
                  </AppLayout>
                </PrivateRoute>
              }
            />
          </>
        )}

        {/* Jury Portal */}

        {(isJuryMember || hasAllRoles) && (
          <>
            <Route
              path={`${routes.juryPortal}`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <JuryPortal />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path={`${routes.assignedStartups}`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <AssignedStartups />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path={`${routes.juryPortal}/:Id`}
              element={
                <PrivateRoute>
                  <AppLayout>
                    <StartupDetails />
                  </AppLayout>
                </PrivateRoute>
              }
            />
          </>
        )}

        {/* /turbostart was a second mount of the same portal while it lived
            outside the app's route conventions. /home is now the single mount,
            so this only redirects — one component, one rendering route. Kept
            rather than deleted so links already shared to /turbostart resolve
            instead of falling through to the catch-all. */}
        <Route path={`${routes.turbostart}/*`} element={<Navigate to={routes.home} replace />} />

        <Route
          path="*"
          element={
            <PrivateRoute>
              <AppLayout>
                <Login />
              </AppLayout>
            </PrivateRoute>
          }
        />
      </Routes>
    </>
  );
};

export default MainLayout;
